/**
 * What the media screen knows about a file without rendering anything.
 *
 * React-free on purpose: the grid, the list and the row menu all ask the same
 * three questions about an asset (is it showable, what is it called, how big is
 * it), and answering them in one place is what stops the two views from
 * disagreeing about a file - which is exactly what happened while the list view
 * carried its own copy of the thumbnail rules.
 */
import type { MediaAsset } from "../../../lib/media";

export const isImage = (m: MediaAsset): boolean => m.mimeType.startsWith("image/");
export const isVideo = (m: MediaAsset): boolean => m.mimeType.startsWith("video/");

/**
 * The short badge for a file's kind: "PNG", "MP4", "PDF".
 *
 * The subtype rather than the whole mime, because a column of "image/png" is a
 * column of the word "image" - the part that differs between two rows is the
 * part after the slash.
 */
export function extLabel(m: MediaAsset): string {
  return (m.mimeType.split("/")[1] ?? m.mimeType).toUpperCase();
}

/**
 * Bytes as a human string. A file with no recorded size returns "-" rather than
 * an empty cell: a blank in a size column reads as zero bytes, and an asset
 * uploaded before the field existed is not an empty file.
 */
export function formatBytes(b: number): string {
  if (!b) return "-";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}
